import {
  DataTableSortableColumn,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  TooltipTrigger,
  TooltipContent,
  DropdownMenu,
  Typography,
  ColumnDef,
  Tooltip,
  Button,
  Badge,
} from '@ui/components';
import { EmployeeTableDataDto } from '@interfaces/models';
import { MenuVertical } from '@ui/components/icons';
import { formatPhoneNumber } from '@helpers/utils';
import { TFunction } from 'i18next';

export const getImportEmployeesColumns = (
  t: TFunction,
  onEditEmployee: (employee: EmployeeTableDataDto) => void,
  onRemoveEmployee: (employee: EmployeeTableDataDto) => void,
): ColumnDef<EmployeeTableDataDto>[] => [
  {
    accessorKey: 'firstName',
    header: ({ column }) => {
      return (
        <DataTableSortableColumn
          label={t('ONBOARDING.IMPORT_EMPLOYEES.TABLE_HEADER_FIRST_NAME')}
          column={column}
        />
      );
    },
  },
  {
    accessorKey: 'lastName',
    maxSize: 300,
    header: ({ column }) => {
      return (
        <DataTableSortableColumn
          label={t('ONBOARDING.IMPORT_EMPLOYEES.TABLE_HEADER_LAST_NAME')}
          column={column}
        />
      );
    },
  },
  {
    accessorKey: 'phoneNumber',
    header: ({ column }) => {
      return (
        <DataTableSortableColumn
          label={t('ONBOARDING.IMPORT_EMPLOYEES.TABLE_HEADER_PHONE_NUMBER')}
          column={column}
        />
      );
    },
    cell: ({ row }) => {
      const { phoneNumber, phoneExt } = row.original;
      return formatPhoneNumber(phoneNumber, phoneExt);
    },
  },
  {
    accessorKey: 'email',
    maxSize: 300,
    header: ({ column }) => {
      return (
        <DataTableSortableColumn
          label={t('ONBOARDING.IMPORT_EMPLOYEES.TABLE_HEADER_EMAIL')}
          column={column}
        />
      );
    },
  },
  {
    accessorKey: 'assignedVehicles',
    maxSize: 100,
    header: () => t('ONBOARDING.IMPORT_EMPLOYEES.TABLE_HEADER_ASSIGNED_VEHICLES'),
    cell: ({ row }) => {
      const assignedVehicles = row.original.assignedVehicles ?? [];
      const assignedVehiclesCount = assignedVehicles.length;

      if (!assignedVehiclesCount) {
        return '-';
      }

      const vehiclesText = assignedVehicles.map((vehicle) => vehicle.registrationNumber).join(', ');

      if (assignedVehiclesCount <= 1) {
        return vehiclesText;
      }

      return (
        <Tooltip>
          <TooltipTrigger className="truncate max-w-[100px]">{vehiclesText}</TooltipTrigger>
          <TooltipContent className="bg-background text-default flex gap-2 w-auto max-w-[11rem] p-3">
            <Typography variant="labelMediumBook">{vehiclesText}</Typography>
          </TooltipContent>
        </Tooltip>
      );
    },
  },
  {
    accessorKey: 'labels',
    header: t('ONBOARDING.IMPORT_EMPLOYEES.TABLE_HEADER_LABEL'),
    cell: ({ row }) => {
      const labels = row.original.labels ?? [];
      const labelCount = labels.length;

      if (!labelCount) {
        return '-';
      }

      return (
        <div className="flex items-center gap-1 whitespace-nowrap">
          <Badge size="large" outlined>
            {labels[0]?.name}
          </Badge>
          {labelCount > 1 && (
            <Tooltip>
              <TooltipTrigger>
                <Badge size="large" outlined icon>
                  +{labelCount - 1}
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="bg-background flex gap-2 w-auto max-w-[15rem] flex-wrap">
                {labels.map((label) => (
                  <Badge key={label.id} size="large" outlined>
                    {label.name}
                  </Badge>
                ))}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: 'actions',
    header: '',
    size: 100,
    cell: ({ row }) => {
      const rowData = row.original;

      return (
        <div className="flex justify-end">
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger tabIndex={-1} asChild>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost">
                    <MenuVertical />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent className="w-auto">
                <Typography variant="labelXSmallBook">
                  {t('ONBOARDING.IMPORT_EMPLOYEES.TABLE_MORE_ACTIONS')}
                </Typography>
              </TooltipContent>
            </Tooltip>
            <DropdownMenuContent className="w-56">
              <DropdownMenuItem onClick={() => onEditEmployee(rowData)}>
                {t('ONBOARDING.IMPORT_EMPLOYEES.TABLE_EDIT_EMPLOYEE')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onRemoveEmployee(rowData)}>
                {t('ONBOARDING.IMPORT_EMPLOYEES.TABLE_DELETE_EMPLOYEE')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      );
    },
  },
];
